import type {AreaDeAcceso} from '@cnc/contracts';
import {CargaDelPlaneador} from './planeador';
import type {ReactNode} from 'react';
import {Link, useSearchParams} from 'react-router';
import {useCargas} from '../api/cargas';
import {ActualizarDesdeNavieras} from '../componentes/actualizar-desde-navieras';
import {AYUDA_CARGAS as C} from '../componentes/ayuda-general';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {CargaDeCarpetaDeExportaciones} from '../componentes/carga-carpeta-de-exportaciones';
import {CargaDeViajes} from '../componentes/carga-de-viajes';
import {CargarConsolidado} from '../componentes/cargar-consolidado';
import {CargaDePagos} from '../componentes/cashflow-pagos';
import {nivelDe, usePermisos, useUsuario} from '../componentes/sesion-contexto';
import {CargaDeSeguimientoDore} from './dore';
import {tr} from '../i18n/idioma';
import {fechaHora, numero, textoOGuion} from '../utilidades/formato';
import {CargaDelStatus} from './financiaciones';
import {CargaDeDeclaracionesDeMineros, CargaDeMineros} from './mineros';

// Cargar archivos (menu nuevo, revision del 6/10/2026): todas las cargas en un
// solo lugar con su historial. Cada carga sigue siendo la misma de su pantalla
// (revisar primero, guardar despues); aqui solo se reunen. La que esta abierta
// viaja en la direccion (?abrir=) para que un enlace la abra directo.
type Carga = {
  clave: string;
  titulo: string;
  // Seccion o pestana que debe editar la persona para ver esta carga (decision 0062).
  area: AreaDeAcceso;
  archivo: string;
  destino: string;
  // Se abre aqui mismo o se va a su pantalla.
  panel?: (cerrar: () => void) => ReactNode;
  enlace?: string;
};

export function PaginaCargarArchivos() {
  const permisos = usePermisos();
  const usuario = useUsuario();
  const [parametros, setParametros] = useSearchParams();
  const abierta = parametros.get('abrir');
  const abrir = (clave: string | null) =>
    setParametros(clave ? new URLSearchParams({abrir: clave}) : new URLSearchParams(), {replace: true});
  const cerrar = () => abrir(null);

  const todas: Carga[] = [
    {
      clave: 'consolidado',
      area: 'inventario.consolidado',
      titulo: tr('Consolidated Shipments', 'Consolidated Shipments'),
      archivo: tr('Hojas Storage y Exported.', 'Storage and Exported sheets.'),
      destino: tr(
        'Big Bags nuevos del inventario y diferencias con la app para corregir.',
        'New inventory Big Bags and differences with the app to fix.',
      ),
      panel: c => <CargarConsolidado onCerrar={c} />,
    },
    {
      clave: 'control-de-empaque',
      area: 'inventario.consolidado',
      titulo: tr('Control de empaque', 'Packing control'),
      archivo: tr('El formato de una recolección.', 'The form of one pickup.'),
      destino: tr('Big Bags de la recolección en el inventario.', 'Big Bags of the pickup in the inventory.'),
      enlace: '/inventario/cargas',
    },
    {
      clave: 'status',
      area: 'exportaciones.seguimiento',
      titulo: tr('Status o Excel para editar', 'Status or editable Excel'),
      archivo: tr(
        'Status Concentrates Shipments (hoja Tracking Status) o el Excel que se descarga del seguimiento.',
        'Status Concentrates Shipments (Tracking Status sheet) or the Excel downloaded from tracking.',
      ),
      destino: tr('Datos de seguimiento de las exportaciones.', 'Export tracking data.'),
      enlace: '/seguimiento/cambios',
    },
    {
      clave: 'rastreador',
      area: 'exportaciones.navieras',
      titulo: tr('Rastreador de navieras', 'Shipping-line tracker'),
      archivo: tr(
        'Shipment_output.xlsx y los PDF o datos que guarda el rastreador.',
        'Shipment_output.xlsx and the PDFs or data the tracker saves.',
      ),
      destino: tr('ETD, ETA, puerto actual y estado de llegada.', 'ETD, ETA, current port and arrival status.'),
      panel: c => <ActualizarDesdeNavieras onCerrar={c} />,
    },
    {
      clave: 'reserva',
      area: 'exportaciones.reservas',
      titulo: tr('Reserva (PDF del booking)', 'Booking (PDF)'),
      archivo: tr('La confirmación de la naviera en PDF.', "The shipping line's confirmation as PDF."),
      destino: tr(
        'Reservas, contenedores y alertas de 20, 16 y 3 días.',
        'Bookings, containers and 20/16/3-day alerts.',
      ),
      enlace: '/seguimiento/reservas',
    },
    {
      clave: 'carpeta-de-exportaciones',
      area: 'exportaciones.documentos',
      titulo: tr('Carpeta de exportaciones', 'Exports folder'),
      archivo: tr(
        'La carpeta de las exportaciones de Dropbox, o la de una sola.',
        'The Dropbox exports folder, or the folder of a single export.',
      ),
      destino: tr(
        'El expediente de cada exportación; las facturas en PDF se ligan a sus Big Bags.',
        'The export file of each export; PDF invoices are linked to their Big Bags.',
      ),
      panel: c => <CargaDeCarpetaDeExportaciones onCerrar={c} />,
    },
    {
      clave: 'financiaciones',
      area: 'exportaciones.financiaciones',
      titulo: tr('Status BLQ', 'BLQ Status'),
      archivo: tr(
        'BLQ Weekly Status y, si lo tienes, el Intercompany.',
        'BLQ Weekly Status and, if you have it, the Intercompany.',
      ),
      destino: tr('Financiaciones y cupos.', 'Financing and credit lines.'),
      panel: () => <CargaDelStatus abiertaAlInicio />,
    },
    {
      clave: 'pagos-terceros',
      area: 'cashflow',
      titulo: tr('Payment Schedule Third Parties', 'Payment Schedule Third Parties'),
      archivo: tr('Hojas Input y Payment Schedule.', 'Input and Payment Schedule sheets.'),
      destino: tr(
        'Cashflow: pagos a proveedores terceros y acuerdos.',
        'Cashflow: third-party payments and agreements.',
      ),
      panel: () => <CargaDePagos abiertaAlInicio />,
    },
    {
      clave: 'mineros',
      area: 'inventario.mineros',
      titulo: tr('Archivos de Mineros', 'Mineros files'),
      archivo: tr(
        'BD Envíos de la planta, base de la bodega, listas de empaque y declaraciones de valor.',
        'Plant BD Envíos, warehouse database, packing lists and value declarations.',
      ),
      destino: tr('Inventario de Mineros.', 'Mineros inventory.'),
      panel: () => <CargaDeMineros abiertaAlInicio />,
    },
    {
      clave: 'declaraciones-mineros',
      area: 'inventario.mineros',
      titulo: tr('Declaraciones de valor de Mineros (TM)', 'Mineros value declarations (TM)'),
      archivo: tr(
        'Los PDF de Recolecciones Mineros → Ingreso TM → Declaracion Valor TM.',
        'The PDFs in Recolecciones Mineros → Ingreso TM → Declaracion Valor TM.',
      ),
      destino: tr(
        'Inventario de Mineros: los Big Bags pasan de la planta a la bodega de Cartagena.',
        'Mineros inventory: Big Bags move from the plant to the Cartagena warehouse.',
      ),
      panel: c => <CargaDeDeclaracionesDeMineros onCerrar={c} />,
    },
    {
      clave: 'viajes',
      area: 'inventario.recolecciones',
      titulo: tr('Viajes de camión', 'Truck trips'),
      archivo: tr('El Excel para editar de las recolecciones.', 'The editable Excel of the pickups.'),
      destino: tr('Viajes, conductores y fechas de ingreso a bodega.', 'Trips, drivers and warehouse entry dates.'),
      panel: c => <CargaDeViajes onCerrar={c} />,
    },
    {
      clave: 'planeador',
      area: 'planeador',
      titulo: tr('Planificador Semanal Bulkmatic', 'Bulkmatic Weekly Planner'),
      archivo: tr(
        'Uno o varios Excel del planificador (hoja Planificador horario semanal).',
        'One or more planner Excel files (Planificador horario semanal sheet).',
      ),
      destino: tr(
        'Planeador semanal: las operaciones de la bodega por día y hora, de histórico.',
        'Weekly planner: warehouse operations by day and time, as history.',
      ),
      panel: c => <CargaDelPlaneador onCerrar={c} />,
    },
    // Dore (decision 0057): solo administradores.
    ...(permisos.administrar
      ? ([
          {
            clave: 'dore',
            area: 'dore',
            titulo: tr('Seguimiento de doré', 'Doré tracking'),
            archivo: tr(
              'Seguimiento.xlsx de DORE EXPORTS (hoja Data).',
              'Seguimiento.xlsx from DORE EXPORTS (Data sheet).',
            ),
            destino: tr(
              'Doré: exportaciones, cobros, pagos, ensayos y liquidaciones.',
              'Doré: exports, collections, payments, assays and settlements.',
            ),
            panel: () => <CargaDeSeguimientoDore abiertaAlInicio />,
          },
        ] satisfies Carga[])
      : []),
  ];
  const cargas = todas.filter(c => nivelDe(usuario, c.area) === 'editar');
  const elegida = cargas.find(c => c.clave === abierta && c.panel);

  return (
    <section className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-verde-oscuro">{tr('Cargar archivos', 'Upload files')}</h1>
        <p className="text-sm text-apagado">
          {tr(
            'Todas las cargas en un solo lugar. Cada una primero muestra qué cambia y no guarda nada hasta que confirmes.',
            'All uploads in one place. Each one first shows what changes and saves nothing until you confirm.',
          )}
        </p>
      </div>

      {!permisos.editar || !cargas.length ? (
        <p className="rounded-lg border border-linea bg-white p-4 text-sm text-apagado">
          {tr(
            'Tu rol es de consulta: puedes ver el historial de cargas, pero no cargar archivos.',
            'Your role is read-only: you can see the upload history, but not upload files.',
          )}
        </p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" data-prueba="cargas-disponibles">
          {cargas.map(c => {
            const activa = elegida?.clave === c.clave;
            const clase = `flex h-full flex-col rounded-lg border bg-white p-4 text-left text-sm transition ${
              activa ? 'border-verde ring-1 ring-verde' : 'border-linea hover:border-verde'
            }`;
            const contenido = (
              <>
                <span className="font-medium text-verde-oscuro">{c.titulo}</span>
                <span className="mt-1 text-xs text-apagado">{c.archivo}</span>
                <span className="mt-2 text-xs">
                  <span className="text-apagado">{tr('Actualiza: ', 'Updates: ')}</span>
                  {c.destino}
                </span>
                <span className="mt-auto pt-3 text-xs font-medium text-verde">
                  {c.enlace
                    ? tr('Ir a cargar →', 'Go to upload →')
                    : activa
                      ? tr('Abierta ↓', 'Open ↓')
                      : tr('Cargar ↓', 'Upload ↓')}
                </span>
              </>
            );
            return c.enlace ? (
              <Link key={c.clave} to={c.enlace} className={clase}>
                {contenido}
              </Link>
            ) : (
              <button
                key={c.clave}
                type="button"
                onClick={() => abrir(activa ? null : c.clave)}
                aria-expanded={activa}
                className={clase}
              >
                {contenido}
              </button>
            );
          })}
        </div>
      )}

      {permisos.editar && elegida?.panel && <div>{elegida.panel(cerrar)}</div>}

      <Historial />
    </section>
  );
}

function Historial() {
  const cargas = useCargas();
  return (
    <div className="rounded-lg border border-linea bg-white p-4" data-prueba="historial-de-cargas">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-medium">{tr('Últimas cargas', 'Latest uploads')}</h2>
        <Link to="/calidad" className="text-xs text-verde hover:underline">
          {tr('Ver incidencias en Calidad de datos →', 'See issues in Data quality →')}
        </Link>
      </div>
      {cargas.isError && <p className="mt-2 text-sm text-alerta">{cargas.error.message}</p>}
      {cargas.data && cargas.data.cargas.length === 0 && (
        <p className="mt-2 text-sm text-apagado">{tr('Todavía no hay cargas.', 'There are no uploads yet.')}</p>
      )}
      {cargas.data && cargas.data.cargas.length > 0 && (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs tracking-wide text-apagado uppercase">
                <th className="py-1 pr-3 font-semibold">
                  {tr('Fecha', 'Date')}
                  <NotaDeAyuda texto={C.fecha} />
                </th>
                <th className="py-1 pr-3 font-semibold">
                  {tr('Tipo', 'Type')}
                  <NotaDeAyuda texto={C.tipo} />
                </th>
                <th className="py-1 pr-3 font-semibold">
                  {tr('Archivo', 'File')}
                  <NotaDeAyuda texto={C.archivo} />
                </th>
                <th className="py-1 pr-3 font-semibold">
                  {tr('Estado', 'Status')}
                  <NotaDeAyuda texto={C.estado} />
                </th>
                <th className="py-1 pr-3 text-right font-semibold">
                  {tr('Filas', 'Rows')}
                  <NotaDeAyuda texto={C.filas} />
                </th>
                <th className="py-1 text-right font-semibold">
                  {tr('Nuevas / actualizadas', 'New / updated')}
                  <NotaDeAyuda texto={C.nuevasActualizadas} />
                </th>
              </tr>
            </thead>
            <tbody>
              {cargas.data.cargas.map(c => (
                <tr key={c.id} className="border-t border-linea/70">
                  <td className="py-1 pr-3 whitespace-nowrap">{fechaHora(c.creadaEl)}</td>
                  <td className="py-1 pr-3">{c.tipoEtiqueta}</td>
                  <td className="max-w-72 truncate py-1 pr-3" title={c.archivo ?? ''}>
                    {textoOGuion(c.archivo)}
                  </td>
                  <td className={`py-1 pr-3 ${c.estado === 'failed' ? 'text-alerta' : ''}`}>
                    {c.estadoEtiqueta}
                    {c.error ? ` · ${c.error}` : ''}
                  </td>
                  <td className="py-1 pr-3 text-right tabular-nums">{numero(c.filas)}</td>
                  <td className="py-1 text-right tabular-nums">
                    {c.nuevas === null && c.actualizadas === null
                      ? '—'
                      : `${numero(c.nuevas ?? 0)} / ${numero(c.actualizadas ?? 0)}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
